import React, { useEffect, useState,useRef } from 'react';
import { FaEye, FaEdit, FaBan, FaDownload, FaPlus, FaSort, FaSortUp, FaSortDown, FaCheck, FaSearch, FaCog } from 'react-icons/fa';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import DepartmentFormModal from './DepartmentFormModal';
import DepartmentViewModal from './DepartmentViewModal';
import Sidebar from './Sidebar';
import { useNavigate } from 'react-router-dom';

const LeftArrowIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"/></svg>;
const RightArrowIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z"/></svg>;

const API_URL = '/api';


 
/* Helper to read the persisted (last-saved) mode from flags */
const getSavedModeFromFlags = ({ ShowOnHeader, ShowOnItem }) => {
  if (ShowOnHeader && ShowOnItem) return 'both';
  if (ShowOnHeader) return 'header';
  if (ShowOnItem) return 'details';
  return 'none';
};
 
/* -------- Custom checkbox dropdown for Show Area -------- */
const useClickOutside = (ref, onOutside) => {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onOutside?.();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onOutside]);
};
 
/* ======= UPGRADED: ShowAreaDropdown (UI-only) ======= */
const ShowAreaDropdown = ({
  disabled,
  screenName,
  value, // { ShowOnHeader, ShowOnItem }
  onChange,
  frozen = false // NEW: if true, dropdown disabled / can't change
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useClickOutside(wrapRef, () => setOpen(false));
 
  const ShowOnHeader = !!value?.ShowOnHeader;
  const ShowOnItem = !!value?.ShowOnItem;
  const both = ShowOnHeader && ShowOnItem;
 
  const label = both ? 'Both' : ShowOnHeader ? 'Show on Header' : ShowOnItem ? 'Show on Item' : '— Select —';
 
  const toggleHeader = () => {
    if (frozen) return;
    onChange(screenName, { ShowOnHeader: !ShowOnHeader, ShowOnItem });
  };
  const toggleItem = () => {
    if (frozen) return;
    onChange(screenName, { ShowOnHeader, ShowOnItem: !ShowOnItem });
  };
  const setBoth = () => {
    if (frozen) return;
    onChange(screenName, { ShowOnHeader: true, ShowOnItem: true });
  };
 
  return (
    <div className="relative inline-block text-left" ref={wrapRef}>
      <button
        type="button"
        disabled={disabled || frozen}
        onClick={() => setOpen((o) => !o)}
        className={`min-w-[200px] inline-flex items-center justify-between gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm transition
        ${(disabled || frozen) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#2c3e50]'}`}
        title={frozen ? 'This row is frozen' : 'Show Area'}
      >
        <span className={`truncate ${label === '— Select —' ? 'text-gray-400' : 'text-gray-700'}`}>{label}</span>
        <svg className={`h-4 w-4 ${(disabled || frozen) ? 'text-gray-300' : 'text-gray-500'}`} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>
 
      {open && !disabled && !frozen && (
        <div
          className="absolute z-30 mt-2 w-72 max-h-64 overflow-auto rounded-2xl border border-gray-200 bg-white shadow-2xl"
          role="menu"
        >
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase">Choose visibility</p>
          </div>
 
          <div className="py-1">
            <button
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition text-sm"
              onClick={toggleHeader}
              role="menuitem"
            >
              <input type="checkbox" readOnly checked={ShowOnHeader} className="h-4 w-4 rounded text-[#2c3e50] focus:ring-[#2c3e50]" />
              <span>Show on Header</span>
            </button>
            <button
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition text-sm"
              onClick={toggleItem}
              role="menuitem"
            >
              <input type="checkbox" readOnly checked={ShowOnItem} className="h-4 w-4 rounded text-[#2c3e50] focus:ring-[#2c3e50]" />
              <span>Show on Item</span>
            </button>
            <div className="my-1 border-t border-gray-100" />
            <button
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition text-sm"
              onClick={setBoth}
              role="menuitem"
            >
              <input type="checkbox" readOnly checked={both} className="h-4 w-4 rounded text-[#2c3e50] focus:ring-[#2c3e50]" />
              <span>Both</span>
            </button>
          </div>
        </div>
      )}
      {frozen && (
        <div className="absolute right-0 mt-1 text-[11px] text-yellow-700">frozen</div>
      )}
    </div>
  );
};
 
/* ======= UPGRADED: ScreenConfigModal (UI-only, with reliable scrolling) ======= */
/* ======= FULLSCREEN ScreenConfigModal ======= */
const ScreenConfigModal = ({ open, onClose, screens, onSubmit }) => {
  const [selectedScreens, setSelectedScreens] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // NEW: local search state for screens
  const [screenSearch, setScreenSearch] = useState('');
 
  const getCombinedValue = (s) => {
    if (!s) return 'none';
    if (s.ShowOnHeader && s.ShowOnItem) return 'both';
    if (s.ShowOnHeader) return 'header';
    if (s.ShowOnItem) return 'details';
    return 'none';
  };
 
  useEffect(() => {
    if (open) {
      const initialSelectedScreens = {};
      screens.forEach(screen => {
        const savedMode = getSavedModeFromFlags(screen);
        initialSelectedScreens[screen.ScreenName] = {
          ShowOnScreen: screen.ShowOnScreen,
          ShowOnHeader: screen.ShowOnHeader,
          ShowOnItem: screen.ShowOnItem,
          HeaderTable: screen.HeaderTable,
          ItemTable: screen.ItemTable,
          savedMode,
          // NEW: freeze row immediately if persisted savedMode is 'both'
          frozen: savedMode === 'both'
        };
      });
      setSelectedScreens(initialSelectedScreens);
      setScreenSearch(''); // reset local search when opened
    }
  }, [open, screens]);
 
  const handleScreenChange = (screenName, field, value) => {
    setSelectedScreens(prev => {
      const updated = { ...prev };
      const row = updated[screenName];
      if (!row) return prev;
      // if frozen, do not allow changes
      if (row.frozen) return prev;
 
      if (field === 'ShowOnScreen' && !value) {
        updated[screenName] = {
          ...row,
          ShowOnScreen: false,
          ShowOnHeader: false,
          ShowOnItem: false
        };
      } else if (field === 'ShowOnScreen' && value) {
        updated[screenName] = { ...row, ShowOnScreen: true };
      } else {
        updated[screenName] = { ...row, [field]: value };
      }
      return updated;
    });
  };
 
  const handleShowAreaPartial = (screenName, { ShowOnHeader, ShowOnItem }) => {
    setSelectedScreens(prev => {
      const curr = prev[screenName] || {};
      // don't modify a frozen row
      if (curr.frozen) return prev;
      const ShowOnScreen = (ShowOnHeader || ShowOnItem) ? true : curr.ShowOnScreen;
      return {
        ...prev,
        [screenName]: {
          ...curr,
          ShowOnScreen,
          ShowOnHeader,
          ShowOnItem
        }
      };
    });
  };
 
  const handleSubmit = async () => {
    setIsSubmitting(true);
 
    try {
      const screensToUpdate = Object.keys(selectedScreens).map(screenName => ({
        ScreenName: screenName,
        ShowOnScreen: selectedScreens[screenName].ShowOnScreen,
        ShowOnHeader: selectedScreens[screenName].ShowOnHeader,
        ShowOnItem: selectedScreens[screenName].ShowOnItem
      }));
 
      for (const screen of screensToUpdate) {
        const response = await fetch(`${API_URL}/screens/${encodeURIComponent(screen.ScreenName)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ShowOnScreen: screen.ShowOnScreen,
            ShowOnHeader: screen.ShowOnHeader,
            ShowOnItem: screen.ShowOnItem
          })
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `Failed to update screen ${screen.ScreenName}`);
        }
      }
 
      // After successful save, update savedMode and freeze rows that are now 'both'
      setSelectedScreens(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(name => {
          const row = next[name];
          const combinedNow = getCombinedValue(row);
          next[name] = {
            ...row,
            savedMode: combinedNow,
            // freeze permanently if combinedNow is 'both'
            frozen: combinedNow === 'both' ? true : row.frozen || false
          };
        });
        return next;
      });
 
      await onSubmit(screensToUpdate);
      alert('Screen configuration updated successfully');
      onClose();
    } catch (error) {
      console.error('Error updating screen configuration:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };
 
  if (!open) return null;
 
  // NEW: filter screens using screenSearch (case-insensitive)
  const displayedScreens = (screens || []).filter(s => s.ScreenName.toLowerCase().includes(screenSearch.toLowerCase()));
 
  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white sticky top-0 z-20">
        <div>
          <h2 className="text-xl font-extrabold text-[#2c3e50]">Screen Configuration</h2>
          <p className="text-xs text-gray-500 mt-0.5">Manage visibility settings for all screens</p>
        </div>

        {/* NEW: search input placed at the top-right inside modal header */}
        <div className="flex items-right gap-3 mr-4">
          <div className="relative">
            <FaSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search screens"
              value={screenSearch}
              onChange={(e) => setScreenSearch(e.target.value)}
              className="pl-8 pr-3 py-2 border border-gray-200 rounded-lg w-52 focus:outline-none focus:ring-2 focus:ring-[#2c3e50]"
            />
          </div>
           <button
          onClick={onClose}
          className="h-9 w-9 rounded-full text-gray-500 hover:bg-gray-100 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#2c3e50]"
        >
          <span className="text-2xl leading-none">×</span>
        </button>
        </div>

       
      </div>
 
      {/* Content area with scroll */}
      <div className="flex-1 overflow-y-auto px-6 py-6 bg-gray-50">
        <div className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wide min-w-[220px]">Screen Name</th>
                <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Show On Screen</th>
                <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Show Area</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {displayedScreens.map((screen) => {
                const s = selectedScreens[screen.ScreenName] || {};
                const disabledDropdown = !s?.ShowOnScreen || !!s?.frozen;
 
                return (
                  <tr key={screen.ScreenName} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 min-w-[220px]">
                      {screen.ScreenName}
                      {s?.savedMode && s.savedMode !== 'none' && (
                        <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#BDDDFC]/60 text-[#2c3e50] font-semibold">
                          saved: {s.savedMode}
                        </span>
                      )}
                      {s?.frozen && (
                        <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 font-semibold">frozen</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <label className={`inline-flex items-center gap-2 cursor-pointer select-none ${s?.frozen ? 'opacity-60 cursor-not-allowed' : ''}`}>
                        <input
                          type="checkbox"
                          checked={s?.ShowOnScreen || false}
                          onChange={(e) => handleScreenChange(screen.ScreenName, 'ShowOnScreen', e.target.checked)}
                          className="h-4 w-4 text-[#2c3e50] rounded focus:ring-[#2c3e50]"
                          disabled={!!s?.frozen}
                        />
                        <span className="text-sm">Enable</span>
                      </label>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <ShowAreaDropdown
                        disabled={disabledDropdown}
                        screenName={screen.ScreenName}
                        value={{ ShowOnHeader: s?.ShowOnHeader, ShowOnItem: s?.ShowOnItem }}
                        onChange={(name, partial) => handleShowAreaPartial(name, partial)}
                        frozen={!!s?.frozen}
                      />
                    </td>
                  </tr>
                );
              })}
              {displayedScreens.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center py-10 text-gray-500 font-semibold">
                    No screens found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
 
      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200 bg-white sticky bottom-0 flex justify-end gap-2">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#2c3e50]"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="px-4 py-2 rounded-xl border border-transparent text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-600"
        >
          {isSubmitting ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
};
const DepartmentMaster = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const navigate = useNavigate();
  const [showScreenConfig, setShowScreenConfig] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('All');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [screens, setScreens] = useState([]);
  const [isConfigSubmitting, setIsConfigSubmitting] = useState(false);

  dayjs.extend(utc);
  dayjs.extend(timezone);

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // --- THEME CSS VARIABLES: set page background & table header to #EEF6F5 ---
  useEffect(() => {
    const themeVars = {
      "--sidebar-line-color": "#e6efee",
      "--sidebar-bg": "#ffffff",
      "--accent-color": "#2b8f85",    // primary accent (used by primary buttons)
      "--accent-hover": "#23756e",    // hover for primary buttons
      "--page-border-color": "#e6efee",
      "--text-primary": "#334155",
      "--text-muted": "#475569",
      "--text-muted-2": "#64748b",
      "--disabled-color": "#bcd8d5",
      "--sort-inactive": "#cddfe0",
      "--search-icon": "#cbd5da",
      // status colors
      "--status-active-bg": "#e6f6f3",
      "--status-active-color": "#0f766e",
      "--status-inactive-bg": "#fafaf9",
      "--status-inactive-color": "#6b7280",
      // page & table header
      "--page-bg": "#EEF6F5",
      "--table-header-bg": "#EEF6F5",
    };
    Object.entries(themeVars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));

    // optional: ensure sticky header text remains readable
    const styleId = 'department-master-mint-overrides';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        /* ensure table header text contrasts on mint background */
        .department-table thead th { color: #334155; }

        /* primary button uses accent color */
        .primary-btn {
          background-color: var(--accent-color);
          color: #ffffff;
          
        }
        .primary-btn:hover { background-color: var(--accent-hover); }

        /* white secondary button (export / config) */
        .secondary-btn {
          background-color: #ffffff;
          color: var(--text-primary);
          border: 1px solid var(--page-border-color);
          
        }
        .secondary-btn:hover { background-color: #f8faf9; }

        /* pagination active */
        .page-active { background-color: var(--accent-color); color: #fff;  }
        .page-inactive { color: var(--accent-color); }
        .page-inactive:hover { background-color: #f7fbfa; }

        /* status badges fallback (kept minimal — you already have styles per component) */
        .status-active { background-color: var(--status-active-bg); color: var(--status-active-color); border-radius: 9999px; padding: 4px 10px; font-size: 12px; display: inline-block; font-weight: 700; }
        .status-inactive { background-color: var(--status-inactive-bg); color: var(--status-inactive-color); border-radius: 9999px; padding: 4px 10px; font-size: 12px; display: inline-block; font-weight: 700; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
    fetchScreens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/departments`);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setDepartments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    }
    setLoading(false);
  };

  const fetchScreens = async () => {
    try {
      const res = await fetch(`${API_URL}/screens`);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setScreens(data.screens || []);
    } catch (error) {
      console.error('Error fetching screens:', error);
      setScreens([]);
    }
  };

   const handleLogout = () => {
    navigate('/login');
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });

    const sortedDepartments = [...departments].sort((a, b) => {
      const av = a[key] ?? '';
      const bv = b[key] ?? '';

      if (av === null) return 1;
      if (bv === null) return -1;

      if (key === 'CreatedAt' || key === 'UpdatedAt') {
        return direction === 'asc' ? new Date(av) - new Date(bv) : new Date(bv) - new Date(av);
      }

      const as = av.toString().toLowerCase();
      const bs = bv.toString().toLowerCase();
      if (as < bs) return direction === 'asc' ? -1 : 1;
      if (as > bs) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setDepartments(sortedDepartments);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1 text-slate-400" />;
    return sortConfig.direction === 'asc' ? <FaSortUp className="ml-1 text-slate-800" /> : <FaSortDown className="ml-1 text-slate-800" />;
  };

  const handleView = (department) => {
    setSelectedDepartment(department);
    setShowView(true);
  };

  const handleEdit = (department) => {
    setSelectedDepartment(department.DepartmentID);
    setIsEdit(true);
    setShowForm(true);
  };

  const handleDeactivate = async (department) => {
    if (!window.confirm('Are you sure you want to deactivate this department?')) return;
    try {
      const response = await fetch(`${API_URL}/departments/${department.DepartmentID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...department, IsActive: false, UpdatedBy: 'current_user' })
      });
      if (!response.ok) {
        const err = await response.json().catch(()=>({message:'Unknown'}));
        throw new Error(err.message || 'Failed to deactivate department');
      }
      fetchDepartments();
    } catch (error) {
      console.error('Error deactivating department:', error);
      alert(error.message || 'An error occurred while deactivating the department');
    }
  };

  const handleReactivate = async (department) => {
    if (!window.confirm('Are you sure you want to reactivate this department?')) return;
    try {
      const response = await fetch(`${API_URL}/departments/${department.DepartmentID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...department, IsActive: true, UpdatedBy: 'current_user' })
      });
      if (!response.ok) {
        const err = await response.json().catch(()=>({message:'Unknown'}));
        throw new Error(err.message || 'Failed to reactivate department');
      }
      fetchDepartments();
    } catch (error) {
      console.error('Error reactivating department:', error);
      alert(error.message || 'An error occurred while reactivating the department');
    }
  };

  const handleDownloadCSV = () => {
    const headers = ['DepartmentCode', 'DepartmentName', 'Status', 'CreatedAt', 'UpdatedAt'];
    const rows = departments.map(d => [
      d.DepartmentCode || '',
      d.DepartmentName || '',
      d.IsActive ? 'Active' : 'Inactive',
      d.CreatedAt ? dayjs(d.CreatedAt).format('YYYY-MM-DD HH:mm') : '',
      d.UpdatedAt ? dayjs(d.UpdatedAt).format('YYYY-MM-DD HH:mm') : ''
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'departments.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCreate = () => {
    setSelectedDepartment(null);
    setIsEdit(false);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData) => {
    if (formLoading) return;
    setFormLoading(true);
    try {
      if (isEdit && selectedDepartment) {
        const response = await fetch(`${API_URL}/departments/${selectedDepartment}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        if (!response.ok) {
          const err = await response.json().catch(()=>({message:'Unknown'}));
          throw new Error(err.message || 'Failed to update department');
        }
      } else {
        const response = await fetch(`${API_URL}/departments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        if (!response.ok) {
          const errorData = await response.json().catch(()=>({message:''}));
          if (errorData.message && errorData.message.includes('duplicate key')) {
            setShowForm(false);
            setTimeout(() => {
              setShowForm(true);
              alert('Duplicate department code detected. Please try again with a different code.');
            }, 100);
            return;
          }
          throw new Error(errorData.message || 'Failed to create department');
        }
      }
      setShowForm(false);
      fetchDepartments();
    } catch (error) {
      console.error('Error saving department:', error);
      alert(error.message || 'An error occurred while saving the department');
    } finally {
      setFormLoading(false);
    }
  };

  const handleScreenConfigSubmit = async (screensToUpdate) => {
    setIsConfigSubmitting(true);
    try {
      for (const screen of screensToUpdate) {
        const response = await fetch(`${API_URL}/screens/${encodeURIComponent(screen.ScreenName)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ShowOnScreen: screen.ShowOnScreen,
            ShowOnHeader: screen.ShowOnHeader,
            ShowOnItem: screen.ShowOnItem
          })
        });
        if (!response.ok) {
          const err = await response.json().catch(()=>({message:'Unknown'}));
          throw new Error(err.message || `Failed to update screen ${screen.ScreenName}`);
        }
      }
      await fetchScreens();
      alert('Screen configuration updated successfully');
    } catch (error) {
      console.error('Error updating screen configuration:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsConfigSubmitting(false);
    }
  };

  const filteredDepartments = (departments || []).filter(department => {
    const matchesSearch = searchTerm === '' ||
      (department.DepartmentCode || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (department.DepartmentName || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = status === 'All' ||
      (status === 'Active' && department.IsActive) ||
      (status === 'Inactive' && !department.IsActive);
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredDepartments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const departmentsForPage = filteredDepartments.slice(startIndex, endIndex);

  const handlePageChange = (page) => setCurrentPage(page);

  const renderPaginationButtons = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    pages.push(
      <button
        key="prev"
        className="p-1 hover:bg-slate-100 disabled:opacity-50 text-slate-800"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <LeftArrowIcon className="w-3 h-3"/>
      </button>
    );

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`px-4 py-1 transition-colors ${currentPage === i ? 'page-active font-semibold' : 'page-inactive'}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    pages.push(
      <button
        key="next"
        className="p-1 hover:bg-slate-100 disabled:opacity-50 text-slate-800"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
      >
        <RightArrowIcon className="w-3 h-3"/>
      </button>
    );

    return pages;
  };

  // Theme object passed to Sidebar so it matches this page's accent/profile color
  const sidebarTheme = {
    text: 'var(--text-primary, #334155)',
    muted: 'var(--text-muted, #475569)',
    border: 'var(--page-border-color, #e6efee)',
    sidebarBorder: 'var(--sidebar-line-color, #e6efee)',
    background: 'var(--sidebar-bg, #ffffff)',
    pageBackground: 'var(--page-bg, #EEF6F5)',
    activeBg: 'var(--accent-color, #2b8f85)',
    profileBg: 'var(--accent-color, #2b8f85)',
    activeText: '#ffffff',
    accent: 'var(--accent-color, #2b8f85)',
    accentHover: 'var(--accent-hover, #23756e)',
    hoverGray: 'rgba(0,0,0,0.04)',
    iconColor: 'var(--accent-color, #2b8f85)',
    sidebarBg: 'var(--sidebar-bg, #ffffff)',
  };

  return (
    <div className="min-h-screen flex font-sans antialiased text-slate-800" style={{ backgroundColor: 'var(--page-bg)' }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={sidebarTheme} onLogout={handleLogout}/>
      <main className={`flex-1 p-10 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>
        {/* Header */}
        <header className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-300 pb-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight leading-tight text-slate-900 mb-1">Department Master</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowScreenConfig(true)}
              className="px-4 py-2 secondary-btn transition-all duration-200 flex items-center gap-2 shadow-sm"
              disabled={isConfigSubmitting}
            >
              <FaCog className="w-4 h-4" />
              {isConfigSubmitting ? 'Processing...' : 'Screen Configuration'}
            </button>

            <button
              className="px-4 py-2 secondary-btn transition-all duration-200 flex items-center gap-2 shadow-sm"
              onClick={handleDownloadCSV}
            >
              <FaDownload className="w-4 h-4" />
              Export
            </button>

            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 primary-btn font-medium hover:shadow transition-all duration-200 shadow-sm"
            >
              <FaPlus className="w-4 h-4" />
              Create New
            </button>
          </div>
        </header>

        {/* Search and Filters (vendor-master style) */}
        <div className="p-6 bg-white border border-gray-300 shadow-sm mb-6" style={{ borderRadius: 0 }}>
          <div className="flex items-end space-x-4">
            <div className="flex-1">
              <label className="block text-slate-700 text-sm font-medium mb-2">What are you looking for?</label>
              <div className="relative mt-2">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search for department code or name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all duration-200 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-2">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-48 py-2 px-3 bg-white border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all duration-200 text-sm"
              >
                <option>All</option>
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </div>
            <button className="px-6 py-2 primary-btn font-medium shadow transition-all duration-200 text-sm" onClick={() => setCurrentPage(1)}>
              Search
            </button>
          </div>
        </div>

        {/* Table (vendor-master attached/flush style) */}
        <div className="flex-1 overflow-hidden bg-white border border-gray-300 shadow-sm" style={{ borderRadius: 0 }}>
          <div className="flex justify-between items-center p-4 border-b border-gray-300">
            <h2 className="text-xl font-semibold text-slate-900">Department Summary</h2>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2 text-slate-700">
                {renderPaginationButtons()}
                <button
                  title="Download CSV"
                  className="p-2 rounded-full text-slate-700 hover:bg-slate-100 transition-all duration-200 hover:scale-110"
                  onClick={handleDownloadCSV}
                >
                  <FaDownload />
                </button>
              </div>
            </div>
          </div>

          <div className="h-full overflow-x-auto scroll-smooth">
            <table className="w-full text-sm department-table">
              <thead style={{ backgroundColor: 'var(--table-header-bg)' }} classname="text-xs text-gray-600 font-semibold uppercase text-left" >
                <tr className="text-slate-700">
                  <th className={`sticky top-0 z-20 px-6 py-4 text-left text-sm font-semibold cursor-pointer transition-all duration-300 ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort('DepartmentCode')}>
                    <div className="flex items-center">
                      DEPARTMENT CODE {getSortIcon('DepartmentCode')}
                    </div>
                  </th>
                  <th className={`sticky top-0 z-20 px-6 py-4 text-left text-sm font-semibold cursor-pointer transition-all duration-300 ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort('DepartmentName')}>
                    <div className="flex items-center">
                      DEPARTMENT NAME {getSortIcon('DepartmentName')}
                    </div>
                  </th>
                  <th className={`sticky top-0 z-20 px-6 py-4 text-left text-sm font-semibold cursor-pointer transition-all duration-300 ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort('IsActive')}>
                    <div className="flex items-center">
                      STATUS {getSortIcon('IsActive')}
                    </div>
                  </th>
                  <th className={`sticky top-0 z-20 px-6 py-4 text-left text-sm font-semibold cursor-pointer transition-all duration-300 ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort('CreatedAt')}>
                    <div className="flex items-center">
                      CREATED AT {getSortIcon('CreatedAt')}
                    </div>
                  </th>
                  <th className={`sticky top-0 z-20 px-6 py-4 text-left text-sm font-semibold cursor-pointer transition-all duration-300 ${isScrolled ? 'shadow' : ''}`} onClick={() => handleSort('UpdatedAt')}>
                    <div className="flex items-center">
                      UPDATED AT {getSortIcon('UpdatedAt')}
                    </div>
                  </th>
                  <th className={`sticky top-0 z-20 px-6 py-4 text-center text-sm font-semibold transition-all duration-300 ${isScrolled ? 'shadow' : ''}`}>
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-slate-600 font-medium">
                      Loading departments...
                    </td>
                  </tr>
                ) : filteredDepartments.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-slate-600 font-medium">
                      No departments found.
                    </td>
                  </tr>
                ) : departmentsForPage.map((department, idx) => (
                  <tr
                    key={department.DepartmentID || idx}
                    className={`transition-all duration-300 group hover:bg-slate-50 bg-white`}
                  >
                    <td className="px-6 py-4 text-left text-sm font-mono whitespace-nowrap">
                      <p className={`text-teal-700 transition-all duration-200 hover:underline ${!department ? 'pointer-events-none' : ''}`} onClick={() => handleView(department)}>{(department.DepartmentCode || '').toUpperCase()}</p>
                    </td>
                    <td className="px-6 py-4 text-left text-sm whitespace-nowrap font-semibold text-slate-900">{(department.DepartmentName || '').toUpperCase()}</td>
                    <td className="px-6 py-4 text-left text-sm">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold shadow-sm transition-all duration-200 ${department.IsActive ? 'bg-teal-50 text-teal-700  hover:bg-teal-100' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}>
                        {(department.IsActive ? 'ACTIVE' : 'INACTIVE')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-left text-sm whitespace-nowrap text-slate-600">
                      {department.CreatedAt ? (
                        <div className="flex flex-col leading-tight uppercase">
                          <span>{dayjs(department.CreatedAt).format('DD MMM YYYY')}</span>
                          <span className="text-xs text-slate-500">{(department.CreatedAt || '').slice(11,16)}</span>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 text-left text-sm whitespace-nowrap text-slate-600">
                      {department.UpdatedAt ? (
                        <div className="flex flex-col leading-tight uppercase">
                          <span>{dayjs(department.UpdatedAt).format('DD MMM YYYY')}</span>
                          <span className="text-xs text-slate-500">{dayjs(department.UpdatedAt).tz('Asia/Kolkata').format('HH:mm')}</span>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 flex gap-2 justify-center items-center">
                      <button title="View" className="p-2 rounded-full text-slate-700 hover:bg-slate-100 transition-all duration-200 hover:scale-110" onClick={() => handleView(department)}><FaEye /></button>
                      <button title="Edit" className="p-2 rounded-full text-teal-700 hover:text-teal-900 hover:bg-teal-50 transition-all duration-200 hover:scale-110" onClick={() => handleEdit(department)}><FaEdit /></button>
                      {department.IsActive ? (
                        <button title="Deactivate" className="p-2 rounded-full text-rose-600 hover:bg-rose-50 hover:text-rose-800 transition-all duration-200 hover:scale-110" onClick={() => handleDeactivate(department)}><FaBan /></button>
                      ) : (
                        <button title="Reactivate" className="p-2 rounded-full text-teal-700 hover:bg-teal-50 hover:text-teal-900 transition-all duration-200 hover:scale-110" onClick={() => handleReactivate(department)}><FaCheck /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modals */}
        <DepartmentFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
          initialData={selectedDepartment}
          isEdit={isEdit}
          loading={formLoading}
        />
        <DepartmentViewModal
          open={showView}
          onClose={() => setShowView(false)}
          department={selectedDepartment}
        />
        <ScreenConfigModal
          open={showScreenConfig}
          onClose={() => setShowScreenConfig(false)}
          screens={screens}
          onSubmit={handleScreenConfigSubmit}
        />
      </main>
    </div>
  )
};
 
export default DepartmentMaster;
